import i18n from "../i18n";
import React, { useState, useEffect, useImperativeHandle, useRef } from "react";
import {
  createStyles,
  Table,
  ScrollArea,
  Select,
  UnstyledButton,
  Group,
  Text,
  Center,
  TextInput,
  Pagination,
  Space,
  Button,
  ActionIcon,
  Paper,
  HoverCard,
  Tooltip,
  Divider,
  Popover,
  Box,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconX,
  IconFilter,
  IconCheck,
  IconInfoCircle,
} from "@tabler/icons";
import AdvancedFilterModal from "./advancedFilterModal";
import _ from "lodash";
import { useForceUpdate } from "@mantine/hooks";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import { buildTeamUserFilterOptions, FILTER_EMPTY_OPTION } from "../helpers/consultantTeamFilter";
import { getColumnFilterSelectedValues } from "../helpers/tableFilterQuery";
import { formatDisplayValue } from "../helpers/phoneUtils";

const noop = () => {};
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const EMPTY_PAGINATION = { total: 10 };

function getTableFilterSelectedValues(tableFilterQuery, col) {
  const field = col.filter?.field;
  if (!field || _.isEmpty(tableFilterQuery) || !_.has(tableFilterQuery, field)) {
    return null;
  }

  const stored = tableFilterQuery[field];
  if (stored === null && String(field).startsWith("_filter")) {
    return [];
  }

  if (stored?.$cleared) {
    return [];
  }

  return getColumnFilterSelectedValues(stored, col);
}

function withEmptyFilterOption(list, col) {
  const normalized = Array.isArray(list) ? [...list] : [];
  if (!col.filter?.includeEmpty) return normalized;

  const hasEmpty = normalized.some((v) => v?.value === FILTER_EMPTY_OPTION.value);
  if (hasEmpty) return normalized;

  return [FILTER_EMPTY_OPTION, ...normalized];
}

function buildUserFilterOptions(users, labelField = "name") {
  return buildTeamUserFilterOptions(users, labelField);
}

const useStyles = createStyles((theme) => ({
  scrollArea: {},

  scrolled: {
    boxShadow: theme.shadows.sm,
  },

  th: {},
  control: {},
  icon: {},

  tableCell: {
    verticalAlign: "top",
  },

  filterRow: {
    cursor: "pointer",
    borderRadius: theme.radius.sm,
    padding: "6px 10px",
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[5]
          : theme.colors.gray[0],
    },
  },

  filterRowSelected: {
    backgroundColor:
      theme.colorScheme === "dark"
        ? theme.fn.rgba(theme.colors.teal[9], 0.25)
        : theme.fn.rgba(theme.colors.teal[0], 0.9),
  },
}));

function valuesEqual(a, b) {
  return _.isEqual(a, b);
}

function buildColumnFilterFetchKey(col) {
  return JSON.stringify({
    field: col.filter?.field,
    apiEntity: col.filter?.apiEntity,
    teamUserIds: col.filter?.teamUserIds,
    userPreQuery: col.filter?.userPreQuery,
    populate: col.filter?.populate,
  });
}

function useColumnDistinctFilter(col, tableFilterQuery) {
  const [api] = useServerApi();
  const [possibleValues, setPossibleValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const loadedFetchKeyRef = useRef(null);
  const inflightFetchKeyRef = useRef(null);
  const { t: translate } = useTranslation();

  const applyFilterList = (list) => {
    const normalized = withEmptyFilterOption(list, col);
    normalized.forEach((v) => {
      v.label = translate(v.label);
    });
    setPossibleValues((prev) => (_.isEqual(prev, normalized) ? prev : normalized));
    col.possibleValues = normalized;

    const nextSelected =
      !_.isEmpty(tableFilterQuery) && _.has(tableFilterQuery, col.filter.field)
        ? getTableFilterSelectedValues(tableFilterQuery, col) ?? []
        : normalized.map((v) => v?.value);
    setSelectedValues((prev) => (_.isEqual(prev, nextSelected) ? prev : nextSelected));
  };

  const fetchUserPreQueryFilterValues = async () => {
    const populate = col.filter?.populate ?? {};
    const select = populate.select ?? "name cname staffId aliasName";
    const labelField = populate.labelField ?? "name";
    const sortField = Object.keys(populate.sort ?? { name: 1 })[0] ?? "name";

    const data = await api.search({
      apiEntity: "user",
      pageSize: 2000,
      currentPage: 1,
      sort: { by: sortField, order: "asc" },
      searchQuery: col.filter.userPreQuery,
      select,
      byAggregation: false,
    });

    applyFilterList(buildUserFilterOptions(data?.docs ?? [], labelField));
  };

  const fetchTeamUserFilterValues = async () => {
    const teamUserIds = col.filter?.teamUserIds ?? [];
    const populate = col.filter?.populate ?? {};
    const select = populate.select ?? "name cname staffId aliasName";
    const labelField = populate.labelField ?? "name";
    const sortField = Object.keys(populate.sort ?? { name: 1 })[0] ?? "name";

    const data = await api.search({
      apiEntity: "user",
      pageSize: Math.max(teamUserIds.length, 1),
      currentPage: 1,
      sort: { by: sortField, order: "asc" },
      searchQuery: { _id: { $in: teamUserIds } },
      select,
      byAggregation: false,
    });

    const users = data?.docs ?? [];
    const idOrder = new Map(teamUserIds.map((id, i) => [String(id), i]));
    users.sort(
      (a, b) =>
        (idOrder.get(String(a._id)) ?? 0) - (idOrder.get(String(b._id)) ?? 0),
    );
    applyFilterList(buildTeamUserFilterOptions(users, labelField));
  };

  const fetchDistinctValues = async () => {
    if (!col.filterable || !col.filter?.field) return;

    const fetchKey = buildColumnFilterFetchKey(col);
    if (loadedFetchKeyRef.current === fetchKey || inflightFetchKeyRef.current === fetchKey) {
      return;
    }

    inflightFetchKeyRef.current = fetchKey;
    try {
      if (col.filter?.staticValues?.length) {
        applyFilterList(col.filter.staticValues);
      } else if (col.filter?.teamUserIds?.length) {
        await fetchTeamUserFilterValues();
      } else if (col.filter?.userPreQuery) {
        await fetchUserPreQueryFilterValues();
      } else {
        const values = await api.getDistinctValues({
          apiEntity: col.filter?.apiEntity,
          field: col.filter?.field,
          populate: col.filter?.populate,
        });
        applyFilterList(values);
      }
      loadedFetchKeyRef.current = fetchKey;
    } catch (e) {
      console.log(e);
    } finally {
      if (inflightFetchKeyRef.current === fetchKey) {
        inflightFetchKeyRef.current = null;
      }
    }
  };

  useDeepCompareEffect(() => {
    if (!col.filterable || !col.filter?.field) return;
    if (col.filter?.staticValues?.length) {
      applyFilterList(col.filter.staticValues);
      return;
    }
    fetchDistinctValues();
  }, [
    col?.filter?.field,
    col?.filter?.teamUserIds,
    col?.filter?.userPreQuery,
    col?.filter?.staticValues,
  ]);

  useEffect(() => {
    if (!col.filterable || !col.filter?.field || !possibleValues.length) return;
    const nextSelected =
      !_.isEmpty(tableFilterQuery) && _.has(tableFilterQuery, col.filter.field)
        ? getTableFilterSelectedValues(tableFilterQuery, col) ?? []
        : possibleValues.map((v) => v?.value);
    setSelectedValues((prev) => (_.isEqual(prev, nextSelected) ? prev : nextSelected));
  }, [tableFilterQuery, possibleValues, col.filter?.field, col.filterable]);

  return {
    possibleValues,
    selectedValues,
    setSelectedValues,
    translate,
  };
}

function useLocalDistinctFilter(col, tableFilterQuery, buildDistinct, dataSource) {
  const [possibleValues, setPossibleValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const { t: translate } = useTranslation();

  const applyFilterList = (list) => {
    const normalized = withEmptyFilterOption(list, col);
    normalized.forEach((v) => {
      v.label = translate(v.label);
    });
    setPossibleValues((prev) => (_.isEqual(prev, normalized) ? prev : normalized));
    col.possibleValues = normalized;

    const nextSelected =
      !_.isEmpty(tableFilterQuery) && _.has(tableFilterQuery, col.filter.field)
        ? getTableFilterSelectedValues(tableFilterQuery, col) ?? []
        : normalized.map((v) => v?.value);
    setSelectedValues((prev) => (_.isEqual(prev, nextSelected) ? prev : nextSelected));
  };

  useDeepCompareEffect(() => {
    if (!col.filterable || !col.filter?.field || !buildDistinct) return;
    const list = buildDistinct(dataSource, col);
    applyFilterList(list);
  }, [col?.filter?.field, dataSource, buildDistinct]);

  useEffect(() => {
    if (!col.filterable || !col.filter?.field || !possibleValues.length) return;
    const nextSelected =
      !_.isEmpty(tableFilterQuery) && _.has(tableFilterQuery, col.filter.field)
        ? getTableFilterSelectedValues(tableFilterQuery, col) ?? []
        : possibleValues.map((v) => v?.value);
    setSelectedValues((prev) => (_.isEqual(prev, nextSelected) ? prev : nextSelected));
  }, [tableFilterQuery, possibleValues, col.filter?.field, col.filterable]);

  return {
    possibleValues,
    selectedValues,
    setSelectedValues,
    translate,
  };
}

function getChipButtonLabel(col, selectedValues, possibleValues, t) {
  const categoryLabel = t(col.headerName ?? _.capitalize(col.field));
  const filterLabel = col.filter?.label ? t(col.filter.label) : categoryLabel;

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  if (!pv.length) return filterLabel;

  const allSelected = sv.length > 0 && sv.length === pv.length;
  const noneSelected = sv.length === 0;

  if (allSelected || noneSelected) {
    return filterLabel;
  }

  const firstVal = sv[0];
  const firstItem = pv.find((v) => valuesEqual(v.value, firstVal));
  const firstLabel = firstItem?.label ?? String(firstVal);
  const extra = sv.length - 1;
  if (extra <= 0) return firstLabel;
  return `${firstLabel} + ${extra}`;
}

function ColumnFilterPanel({
  col,
  onFilter,
  possibleValues,
  selectedValues,
  setSelectedValues,
}) {
  const { t } = useTranslation();
  const { classes, cx, theme } = useStyles();
  const [optionSearch, setOptionSearch] = useState("");

  const w = col.filter?.width ?? 260;
  const h = col.filter?.height ?? 280;

  useEffect(() => {
    setOptionSearch("");
  }, [possibleValues, col?.filter?.field]);

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  const searchLower = optionSearch.trim().toLowerCase();
  const filteredValues = !searchLower
    ? pv
    : pv.filter((v) =>
        String(v.label ?? "")
          .toLowerCase()
          .includes(searchLower),
      );

  const isRowSelected = (val) => sv.some((s) => valuesEqual(s, val));

  const applyFilter = (next) => {
    setSelectedValues(next);
    onFilter(col.filter.field, next, col.possibleValues);
  };

  const toggleValue = (val) => {
    const next = sv.some((s) => valuesEqual(s, val))
      ? sv.filter((s) => !valuesEqual(s, val))
      : [...sv, val];
    applyFilter(next);
  };

  const selectAll = () => {
    const all = pv.map((v) => v.value);
    applyFilter(all);
  };

  const deselectAll = () => {
    applyFilter([]);
  };

  return (
    <Box p={0}>
      <Group position="apart" spacing="xs" px="sm" pt="xs" pb={4} noWrap>
        <UnstyledButton type="button" onClick={selectAll}>
          <Text size="xs" color="teal" weight={500}>
            {t("Select all")}
          </Text>
        </UnstyledButton>
        <UnstyledButton type="button" onClick={deselectAll}>
          <Text size="xs" color="dimmed" weight={500}>
            {t("Deselect all")}
          </Text>
        </UnstyledButton>
      </Group>
      <Divider />
      <Box px="sm" pt="xs" pb={4}>
        <TextInput
          size="xs"
          placeholder={t("Search")}
          icon={<IconSearch size={14} stroke={1.5} />}
          value={optionSearch}
          onChange={(e) => setOptionSearch(e.currentTarget.value)}
          rightSection={
            optionSearch ? (
              <UnstyledButton
                type="button"
                onClick={() => setOptionSearch("")}
                aria-label={t("Clear")}
              >
                <IconX size={14} stroke={1.5} />
              </UnstyledButton>
            ) : null
          }
        />
      </Box>
      <ScrollArea style={{ width: w, height: h }}>
        <Box py="xs" pr={4}>
          {filteredValues.length === 0 && (
            <Text size="xs" color="dimmed" px="sm" py="xs">
              {t("No matches")}
            </Text>
          )}
          {filteredValues.map((v, index) => {
            const selected = isRowSelected(v.value);
            return (
              <UnstyledButton
                key={`${col.filter?.field}-${JSON.stringify(v.value)}`}
                type="button"
                onClick={() => toggleValue(v.value)}
                className={cx(
                  classes.filterRow,
                  selected && classes.filterRowSelected,
                )}
                style={{ display: "block", width: "100%" }}
              >
                <Group position="apart" noWrap spacing="xs">
                  <Text size="xs" style={{ flex: 1 }}>
                    {v.label}
                  </Text>
                  {selected && (
                    <IconCheck
                      size={16}
                      stroke={2}
                      color={theme.colors.teal[6]}
                    />
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Box>
      </ScrollArea>
    </Box>
  );
}

function FilterChip({
  col,
  tableFilterQuery,
  onFilter,
}) {
  const { theme } = useStyles();
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const { possibleValues, selectedValues, setSelectedValues } =
    useColumnDistinctFilter(col, tableFilterQuery);

  const chipText = getChipButtonLabel(
    col,
    selectedValues,
    possibleValues,
    t,
  );

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  const isNoneSelected = pv.length > 0 && sv.length === 0;

  const isPartial =
    pv.length > 0 && sv.length > 0 && sv.length < pv.length;

  const handleFilter = (field, values, pValues) => {
    onFilter(field, values, pValues);
  };

  return (
    <Popover
      position="bottom-start"
      shadow="md"
      trapFocus={false}
      withinPortal
      opened={opened}
      onClose={() => setOpened(false)}
    >
      <Popover.Target>
        <Button
          variant="default"
          size="sm"
          radius="sm"
          compact
          onClick={() => setOpened((o) => !o)}
          rightIcon={
            opened ? (
              <IconChevronUp
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            ) : (
              <IconChevronDown
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            )
          }
          sx={
            isNoneSelected
              ? {
                  borderColor: theme.colors.red[5],
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.fn.rgba(theme.colors.red[9], 0.22)
                      : theme.fn.rgba(theme.colors.red[0], 0.95),
                }
              : isPartial
                ? {
                    borderColor: theme.colors.teal[4],
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.fn.rgba(theme.colors.teal[9], 0.2)
                        : theme.fn.rgba(theme.colors.teal[0], 0.95),
                  }
                : undefined
          }
        >
          <Text
            size="xs"
            weight={500}
            lineClamp={1}
            maw={220}
            color={isNoneSelected ? "red" : undefined}
          >
            {chipText}
          </Text>
        </Button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <ColumnFilterPanel
          col={col}
          tableFilterQuery={tableFilterQuery}
          onFilter={handleFilter}
          possibleValues={possibleValues}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

function LocalFilterChip({
  col,
  tableFilterQuery,
  onFilter,
  buildDistinct,
  dataSource,
}) {
  const { theme } = useStyles();
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);

  const { possibleValues, selectedValues, setSelectedValues } =
    useLocalDistinctFilter(col, tableFilterQuery, buildDistinct, dataSource);

  const chipText = getChipButtonLabel(
    col,
    selectedValues,
    possibleValues,
    t,
  );

  const pv = possibleValues ?? [];
  const sv = selectedValues ?? [];

  const isNoneSelected = pv.length > 0 && sv.length === 0;

  const isPartial =
    pv.length > 0 && sv.length > 0 && sv.length < pv.length;

  const handleFilter = (field, values, pValues) => {
    onFilter(field, values, pValues);
  };

  return (
    <Popover
      position="bottom-start"
      shadow="md"
      trapFocus={false}
      withinPortal
      opened={opened}
      onClose={() => setOpened(false)}
    >
      <Popover.Target>
        <Button
          variant="default"
          size="sm"
          radius="sm"
          compact
          onClick={() => setOpened((o) => !o)}
          rightIcon={
            opened ? (
              <IconChevronUp
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            ) : (
              <IconChevronDown
                size={14}
                stroke={1.5}
                color={
                  isNoneSelected
                    ? theme.colors.red[4]
                    : undefined
                }
              />
            )
          }
          sx={
            isNoneSelected
              ? {
                  borderColor: theme.colors.red[5],
                  backgroundColor:
                    theme.colorScheme === "dark"
                      ? theme.fn.rgba(theme.colors.red[9], 0.22)
                      : theme.fn.rgba(theme.colors.red[0], 0.95),
                }
              : isPartial
                ? {
                    borderColor: theme.colors.teal[4],
                    backgroundColor:
                      theme.colorScheme === "dark"
                        ? theme.fn.rgba(theme.colors.teal[9], 0.2)
                        : theme.fn.rgba(theme.colors.teal[0], 0.95),
                  }
                : undefined
          }
        >
          <Text
            size="xs"
            weight={500}
            lineClamp={1}
            maw={220}
            color={isNoneSelected ? "red" : undefined}
          >
            {chipText}
          </Text>
        </Button>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <ColumnFilterPanel
          col={col}
          onFilter={handleFilter}
          possibleValues={possibleValues}
          selectedValues={selectedValues}
          setSelectedValues={setSelectedValues}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

function LocalFilterChipBar({
  columns,
  tableFilterQuery,
  onFilterChange,
  buildDistinct,
  dataSource,
}) {
  const filterCols = columns.filter((c) => c.filterable && c.filter?.field);
  if (!filterCols.length) return null;

  return (
    <Group spacing="xs" mb="md" sx={{ "& > *": { flexShrink: 0 } }}>
      {filterCols.map((col, i) => (
        <LocalFilterChip
          key={`${col.field}-${i}`}
          col={col}
          tableFilterQuery={tableFilterQuery}
          onFilter={onFilterChange}
          buildDistinct={buildDistinct}
          dataSource={dataSource}
        />
      ))}
    </Group>
  );
}

function FilterChipBar({
  columns,
  tableFilterQuery,
  onFilterChange,
}) {
  const filterCols = columns.filter((c) => c.filterable && c.filter?.field);
  if (!filterCols.length) return null;

  return (
    <Group spacing="xs" mb="md" sx={{ "& > *": { flexShrink: 0 } }}>
      {filterCols.map((col, i) => (
        <FilterChip
          key={`${col.field}-${i}`}
          col={col}
          tableFilterQuery={tableFilterQuery}
          onFilter={onFilterChange}
        />
      ))}
    </Group>
  );
}

function HeaderTip({ tooltip }) {
  if (!tooltip) return null;
  return (
    <Tooltip
      label={tooltip}
      multiline
      width={280}
      withArrow
      withinPortal
      position="top"
      events={{ hover: true, focus: true, touch: true }}
    >
      <ActionIcon
        variant="transparent"
        size="sm"
        aria-label="Column help"
        onClick={(event) => event.stopPropagation()}
        sx={{ cursor: "help" }}
      >
        <IconInfoCircle size={14} stroke={1.5} />
      </ActionIcon>
    </Tooltip>
  );
}

function ThPlain({
  children,
  reversed,
  sorted,
  onSort,
  col,
}) {
  const { classes } = useStyles();

  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th
      className={classes.th}
      width={col.width}
      style={col.minWidth ? { minWidth: col.minWidth } : undefined}
    >
      <Group spacing={4} noWrap>
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group position="apart" noWrap>
            <Text weight={500} size="xs">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
        <HeaderTip tooltip={col.headerTooltip} />
      </Group>
    </th>
  );
}

function ThWithInlineFilter({
  children,
  reversed,
  sorted,
  onSort,
  onFilter,
  col,
  tableFilterQuery,
}) {
  const { classes } = useStyles();

  const { possibleValues, selectedValues, setSelectedValues } =
    useColumnDistinctFilter(col, tableFilterQuery);

  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  return (
    <th
      className={classes.th}
      width={col.width}
      style={col.minWidth ? { minWidth: col.minWidth } : undefined}
    >
      <Group spacing={"xs"} noWrap>
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group position="apart" noWrap>
            <Text weight={500} size="xs">
              {children}
            </Text>
            <Center className={classes.icon}>
              <Icon size={14} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
        <HeaderTip tooltip={col.headerTooltip} />
        <HoverCard shadow="md" width={col.filter.width ?? 200}>
          <HoverCard.Target>
            <Group spacing={1} noWrap>
              <ActionIcon variant="transparent" title="Filter" size="sm">
                <IconFilter size={14} stroke={1.5} />
              </ActionIcon>
              {selectedValues?.length !== possibleValues?.length && (
                <Text size="xs">({selectedValues?.length})</Text>
              )}
            </Group>
          </HoverCard.Target>
          <HoverCard.Dropdown p={0}>
            <ColumnFilterPanel
              col={col}
              onFilter={onFilter}
              possibleValues={possibleValues}
              selectedValues={selectedValues}
              setSelectedValues={setSelectedValues}
            />
          </HoverCard.Dropdown>
        </HoverCard>
      </Group>
    </th>
  );
}

function Th({
  children,
  reversed,
  sorted,
  onSort,
  onFilter,
  col,
  tableFilterQuery,
  columnFilterLayout,
}) {
  const useInlineFilter =
    col.filterable &&
    col.filter?.field &&
    columnFilterLayout === "header";

  if (useInlineFilter) {
    return (
      <ThWithInlineFilter
        reversed={reversed}
        sorted={sorted}
        onSort={onSort}
        onFilter={onFilter}
        col={col}
        tableFilterQuery={tableFilterQuery}
      >
        {children}
      </ThWithInlineFilter>
    );
  }

  return (
    <ThPlain reversed={reversed} sorted={sorted} onSort={onSort} col={col}>
      {children}
    </ThPlain>
  );
}

const ThinHorizontalScroll = ({ viewportRef, minWidth, children }) => {
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [metrics, setMetrics] = useState({
    scrollLeft: 0,
    clientWidth: 0,
    scrollWidth: minWidth || 0,
  });

  const readMetrics = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    setMetrics({
      scrollLeft: viewport.scrollLeft,
      clientWidth: viewport.clientWidth,
      scrollWidth: viewport.scrollWidth,
    });
    viewport.style.setProperty(
      "--table-viewport-width",
      `${viewport.clientWidth}px`
    );
  };

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    readMetrics();
    const observer = new ResizeObserver(readMetrics);
    observer.observe(viewport);
    if (viewport.firstElementChild) observer.observe(viewport.firstElementChild);
    return () => observer.disconnect();
  }, [viewportRef, minWidth]);

  const maxScroll = Math.max(0, metrics.scrollWidth - metrics.clientWidth);
  const canScroll = maxScroll > 1;
  const trackWidth = Math.max(0, metrics.clientWidth);
  const thumbWidth = canScroll
    ? Math.max(72, (metrics.clientWidth / metrics.scrollWidth) * trackWidth)
    : trackWidth;
  const thumbTravel = Math.max(0, trackWidth - thumbWidth);
  const thumbLeft = maxScroll ? (metrics.scrollLeft / maxScroll) * thumbTravel : 0;

  const scrollToThumb = (clientX) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || thumbTravel <= 0) return;
    const rect = track.getBoundingClientRect();
    const nextLeft = clientX - rect.left - thumbWidth / 2;
    const ratio = Math.min(1, Math.max(0, nextLeft / thumbTravel));
    viewport.scrollLeft = ratio * maxScroll;
  };

  const onThumbPointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startX: event.clientX,
      startLeft: viewportRef.current?.scrollLeft || 0,
    };
  };

  const onThumbPointerMove = (event) => {
    const drag = dragRef.current;
    const viewport = viewportRef.current;
    if (!drag || !viewport || thumbTravel <= 0) return;
    const dx = event.clientX - drag.startX;
    const max = viewport.scrollWidth - viewport.clientWidth;
    viewport.scrollLeft = Math.min(
      max,
      Math.max(0, drag.startLeft + (dx / thumbTravel) * max)
    );
  };

  const onThumbPointerUp = (event) => {
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <Box>
      <Box
        ref={viewportRef}
        onScroll={readMetrics}
        sx={{
          overflowX: "auto",
          overflowY: "hidden",
          overscrollBehaviorX: "contain",
          containerType: "inline-size",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {children}
      </Box>
      {canScroll && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 5,
            pointerEvents: "none",
            marginTop: 12,
          }}
        >
          <Box
            ref={trackRef}
            onPointerDown={(event) => {
              if (event.target !== event.currentTarget) return;
              scrollToThumb(event.clientX);
            }}
            sx={{
              pointerEvents: "auto",
              position: "relative",
              width: "100%",
              height: 18,
              background: "#f1f3f5",
              borderTop: "1px solid #dee2e6",
            }}
          >
            <Box
              onPointerDown={onThumbPointerDown}
              onPointerMove={onThumbPointerMove}
              onPointerUp={onThumbPointerUp}
              onPointerCancel={onThumbPointerUp}
              sx={{
                position: "absolute",
                top: 3,
                left: thumbLeft,
                width: thumbWidth,
                height: 12,
                borderRadius: 999,
                background: "#868e96",
                cursor: "grab",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                touchAction: "none",
                "&:hover": { background: "#495057" },
                "&:active": { cursor: "grabbing", background: "#343a40" },
              }}
            >
              {[0, 1, 2].map((mark) => (
                <Box
                  key={mark}
                  sx={{
                    width: 2,
                    height: 6,
                    borderRadius: 1,
                    background: "#fff",
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const SearchBar = ({
  onChange = console.log,
  searchText,
  advanceFilters,
  onAdvancedFilterClick,
  onClearAdvancedFilters,
  hasAdvancedFilters,
  searchBarExtra = null,
  /** Mantine spacing size; use `xs` when filter chips sit directly below */
  groupMarginBottom = "md",
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Group spacing="xs" mb={groupMarginBottom} noWrap>
        <TextInput
          placeholder={t("Search")}
          icon={<IconSearch size={14} stroke={1.5} />}
          value={searchText}
          onChange={(e) => {
            onChange(e.currentTarget.value);
          }}
          rightSection={
            searchText && (
              <UnstyledButton
                onClick={() => {
                  onChange("");
                }}
              >
                <IconX size={14} stroke={1.5} />{" "}
              </UnstyledButton>
            )
          }
          style={{ flex: 1 }}
        />
        {searchBarExtra}
        {advanceFilters && advanceFilters.length > 0 && (
          <>
            <Button
              variant={hasAdvancedFilters ? "filled" : "default"}
              color="blue"
              size="sm"
              onClick={onAdvancedFilterClick}
              leftIcon={<IconFilter size={16} stroke={1.5} />}
              sx={
                !hasAdvancedFilters
                  ? {
                      borderColor: "#373A40",
                    }
                  : undefined
              }
            >
              {t("Filter")}
            </Button>
            {hasAdvancedFilters && (
              <Tooltip label={t("Clear Advanced Filters")}>
                <ActionIcon
                  variant="light"
                  color="red"
                  size="md"
                  onClick={onClearAdvancedFilters}
                  title={t("Clear Advanced Filters")}
                >
                  <IconX size={16} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            )}
          </>
        )}
      </Group>
    </>
  );
};

const PaginationBar = ({
  pageSize,
  onPageSizeChange,
  pagination,
  onPageChange,
  currentPage,
}) => {
  useEffect(() => {
    pagination.page = currentPage;
  }, [currentPage]);
  const { t: translate } = useTranslation();
  return (
    <>
      {pagination && (
        <Group position="right">
          <Group>
            <Pagination
              size="xs"
              page={pagination.page}
              total={pagination.totalPage}
              siblings={1}
              onChange={onPageChange}
            />
            <Select
              data={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
                { value: 40, label: "40" },
                { value: 50, label: "50" },
              ]}
              value={pageSize}
              onChange={onPageSizeChange}
              size="xs"
            />
            <Text size="xs" color="dimmed">
              {pagination.total} {translate("Records")}
            </Text>
          </Group>
        </Group>
      )}
    </>
  );
};

const DataTable = (
  {
    data = EMPTY_ARRAY,
    columns = EMPTY_ARRAY,
    hidePagination = false,
    hideSearchBar = false,
    minHeight = "200px",
    pageSize = 10,
    pagination = EMPTY_PAGINATION,
    searchText = "",
    onPageSizeChange = noop,
    onPageChange = noop,
    onSearchChange = noop,
    onSortChange = noop,
    onFilterChange = noop,
    onActionBtnClick = noop,
    onRowDoubleClick = noop,
    renderSubRow = null,
    tableFilterQuery = EMPTY_OBJECT,
    currentPage = 1,
    padding = "xs",
    tableHeader,
    showRefreshButton = false,
    searchBarExtra = null,
    tdPaddingX = null,
    readonly = false,
    advanceFilters = EMPTY_ARRAY,
    onAdvancedFilterChange = noop,
    advancedFilterQuery = EMPTY_OBJECT,
    columnFilterLayout = "toolbar",
    hideColumnHeaders = false,
    bare = false,
    horizontalScroll = false,
    localFilterMode = false,
    buildDistinct = null,
    filterDataSource = null,
    fillHeight = false,
  },
  ref,
) => {
  useImperativeHandle(ref, () => {
    return {};
  });

  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [advancedFilterModalOpened, setAdvancedFilterModalOpened] =
    useState(false);

  const forceUpdate = useForceUpdate();

  const tableColumns = columns.filter((c) => !c.filterOnly);
  const filterableCols = columns.filter((c) => c.filterable && c.filter?.field);
  const showFilterToolbar =
    columnFilterLayout === "toolbar" && filterableCols.length > 0;
  const tableMinWidth = horizontalScroll
    ? tableColumns.reduce((sum, col) => sum + (col.minWidth || 120), 0)
    : undefined;
  const scrollViewportRef = useRef(null);

  useEffect(() => {
    const resizeObserverErrorHandler = (error) => {
      if (error.message.includes("ResizeObserver loop")) {
        console.warn("ResizeObserver loop error detected and ignored");
      } else {
        console.error(error);
      }
    };

    window.addEventListener("error", resizeObserverErrorHandler);

    return () => {
      window.removeEventListener("error", resizeObserverErrorHandler);
    };
  }, []);

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
  };

  const handleAdvancedFilterClick = () => {
    setAdvancedFilterModalOpened(true);
  };

  const handleAdvancedFilterApply = (query) => {
    onAdvancedFilterChange(query);
  };

  const handleAdvancedFilterClose = () => {
    setAdvancedFilterModalOpened(false);
  };

  const handleClearAdvancedFilters = () => {
    onAdvancedFilterChange({});
  };

  const hasActiveAdvancedFilters = () => {
    return Object.keys(advancedFilterQuery).length > 0;
  };

  const rows = [];
  if (data) {
    data.forEach((d, index) => {
      rows.push(
        <tr
          key={`row-${d?._id || index}`}
          onDoubleClick={
            !onRowDoubleClick
              ? undefined
              : () => onRowDoubleClick({ data: d, rowIndex: index })
          }
          style={onRowDoubleClick ? { cursor: "pointer" } : undefined}
        >
          {tableColumns?.map((col, index_c) => (
            <td
              key={index_c}
              style={{
                verticalAlign: col.isAction ? "middle" : "top",
                textAlign: col.isAction ? "right" : undefined,
                whiteSpace: col.isAction || horizontalScroll ? "nowrap" : undefined,
                ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                ...(tdPaddingX
                  ? { paddingLeft: tdPaddingX, paddingRight: tdPaddingX }
                  : {}),
              }}
            >
              {!col.cellRender && formatDisplayValue(_.get(d, col.field))}
              {col.cellRender && !col.isAction && col.cellRender(col, d)}
              {col.cellRender &&
                col.isAction &&
                col.cellRender(col, index, d, onActionBtnClick)}
            </td>
          ))}
        </tr>
      );
      const subRow = renderSubRow?.(d, tableColumns.length);
      if (subRow) {
        rows.push(
          React.cloneElement(subRow, { key: `sub-${d?._id || index}` })
        );
      }
    });
  }

  useEffect(() => {
    onSortChange(sortBy, reverseSortDirection);
  }, [sortBy, reverseSortDirection]);

  const { t } = useTranslation();

  const TableHeader = () => (
    <thead>
      <tr>
        {tableColumns.map((col, index) => {
          return col.sortable ? (
            <Th
              key={col.field + index}
              sorted={sortBy === col.field}
              reversed={reverseSortDirection}
              onSort={() => setSorting(col.field)}
              onFilter={(field, values, possibleValues) => {
                onFilterChange(field, values, possibleValues);
              }}
              col={col}
              tableFilterQuery={tableFilterQuery}
              columnFilterLayout={columnFilterLayout}
            >
              {!col.isAction &&
                t(col.headerName ?? _.capitalize(col.field))}
            </Th>
          ) : (
            <th
              key={col.field + index}
              style={col.minWidth ? { minWidth: col.minWidth } : undefined}
            >
              <Group spacing={4} noWrap>
                {!col.isAction &&
                  t(col.headerName ?? _.capitalize(col.field))}
                <HeaderTip tooltip={col.headerTooltip} />
              </Group>
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const tableToolbar = (
    <>
      {!hideSearchBar && (
        <>
          <SearchBar
            onChange={onSearchChange}
            searchText={searchText}
            advanceFilters={advanceFilters}
            onAdvancedFilterClick={handleAdvancedFilterClick}
            onClearAdvancedFilters={handleClearAdvancedFilters}
            hasAdvancedFilters={hasActiveAdvancedFilters()}
            groupMarginBottom={showFilterToolbar ? "xs" : "md"}
            searchBarExtra={searchBarExtra}
          />
          {!showFilterToolbar && <Space h={"md"} />}
        </>
      )}
      {showFilterToolbar &&
        (localFilterMode ? (
          <LocalFilterChipBar
            columns={columns}
            tableFilterQuery={tableFilterQuery}
            onFilterChange={onFilterChange}
            buildDistinct={buildDistinct}
            dataSource={filterDataSource ?? data}
          />
        ) : (
          <FilterChipBar
            columns={columns}
            tableFilterQuery={tableFilterQuery}
            onFilterChange={onFilterChange}
          />
        ))}
      {tableHeader && <tableHeader.component {...tableHeader.props} />}
    </>
  );

  const tableElement = horizontalScroll ? (
    <ThinHorizontalScroll viewportRef={scrollViewportRef} minWidth={tableMinWidth}>
      <Box sx={{ minWidth: tableMinWidth }}>
        <Table highlightOnHover fontSize={"xs"}>
          {!hideColumnHeaders && <TableHeader />}
          <tbody>{rows}</tbody>
        </Table>
      </Box>
    </ThinHorizontalScroll>
  ) : (
    <Table highlightOnHover fontSize={"xs"}>
      {!hideColumnHeaders && <TableHeader />}
      <tbody>{rows}</tbody>
    </Table>
  );

  const tableFooter = (
    <>
      {showRefreshButton && (
        <Button
          onClick={() => forceUpdate()}
          size="xs"
          mt="xl"
          variant="light"
        >{i18n.t("Refresh")}</Button>
      )}
      {!fillHeight && <Space h={"xl"} />}
      {!hidePagination && (
        <Box mt={fillHeight ? "sm" : undefined}>
          <PaginationBar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            pagination={pagination}
            onPageChange={onPageChange}
            currentPage={currentPage}
          />
        </Box>
      )}
    </>
  );

  const tableBody = fillHeight ? (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>{tableToolbar}</Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>{tableElement}</Box>
      <Box sx={{ flexShrink: 0 }}>{tableFooter}</Box>
    </Box>
  ) : (
    <>
      {tableToolbar}
      {tableElement}
      {tableFooter}
    </>
  );

  const tableSurface = bare ? (
    tableBody
  ) : (
    <Paper
      p={padding}
      sx={
        fillHeight
          ? {
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }
          : undefined
      }
    >
      {tableBody}
    </Paper>
  );

  return (
    <>
      {fillHeight ? (
        <Box
          sx={{
            flex: 1,
            height: "100%",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tableSurface}
        </Box>
      ) : (
        tableSurface
      )}

      <AdvancedFilterModal
        opened={advancedFilterModalOpened}
        onClose={handleAdvancedFilterClose}
        advanceFilters={advanceFilters}
        onApplyFilters={handleAdvancedFilterApply}
        initialValues={advancedFilterQuery}
      />
    </>
  );
};

export default React.forwardRef(DataTable);
export {
  PaginationBar,
  FilterChipBar,
  LocalFilterChipBar,
  ColumnFilterPanel,
  getTableFilterSelectedValues,
};
